import { RecordUnknown } from '../utils';
import { PickQueryShape } from './pick-query-types';
import { IsQuery, Query } from './query';
import { queryColumnNameToKey } from './query-columns/query-columns';

export abstract class OrchidOrmError extends Error {}

/**
 * When we search for a single record, and it is not found, it can either throw an error, or return `undefined`.
 *
 * Unlike other database libraries, `Orchid ORM` decided to throw errors by default when using methods `take`, `find`, `findBy`, `get` and the record is not found.
 * It is a [good practice](https://github.com/goldbergyoni/nodebestpractices/blob/master/sections/errorhandling/centralizedhandling.md) to catch common errors in a centralized place (see [global error handling](https://orchid-orm.netlify.app/guide/error-handling.html#global-error-handling)), and this allows for a more concise code.
 *
 * If it's more suitable to get the `undefined` value instead of throwing, use `takeOptional`, `findOptional`, `findByOptional`, `getOptional` instead.
 */
export class NotFoundError extends OrchidOrmError {
  // `#query` is private to prevent it from serializing to not cause problems to test runner reports
  // it is exposed with `getQuery` method which prevents this problem from both Vitest and Jest.
  // Exposing it with `get query()` still leaves the issue in Vitest.
  readonly #query: Query;

  constructor(query: IsQuery, message = 'Record is not found') {
    super(message);
    this.#query = query as Query;
  }

  getQuery() {
    return this.#query;
  }
}

export class OrchidOrmInternalError extends Error {
  // `#query` is private to prevent it from serializing to not cause problems to test runner reports
  // it is exposed with `getQuery` method which prevents this problem from both Vitest and Jest.
  // Exposing it with `get query()` still leaves the issue in Vitest.
  readonly #query: Query;

  constructor(
    query: IsQuery,
    message?: string,
    public data?: RecordUnknown,
  ) {
    super(message);
    this.#query = query as Query;
  }

  getQuery() {
    return this.#query;
  }
}

export type QueryErrorName =
  | 'parseComplete'
  | 'bindComplete'
  | 'closeComplete'
  | 'noData'
  | 'portalSuspended'
  | 'replicationStart'
  | 'emptyQuery'
  | 'copyDone'
  | 'copyData'
  | 'rowDescription'
  | 'parameterDescription'
  | 'parameterStatus'
  | 'backendKeyData'
  | 'notification'
  | 'readyForQuery'
  | 'commandComplete'
  | 'dataRow'
  | 'copyInResponse'
  | 'copyOutResponse'
  | 'authenticationOk'
  | 'authenticationMD5Password'
  | 'authenticationCleartextPassword'
  | 'authenticationSASL'
  | 'authenticationSASLContinue'
  | 'authenticationSASLFinal'
  | 'error'
  | 'notice';

export abstract class QueryError<
  T extends PickQueryShape = PickQueryShape,
> extends OrchidOrmInternalError {
  declare message: string;
  declare length?: number;
  declare name: QueryErrorName;
  declare stack: string | undefined;
  code: string | undefined;
  detail: string | undefined;
  severity: string | undefined;
  hint: string | undefined;
  position: string | undefined;
  internalPosition: string | undefined;
  internalQuery: string | undefined;
  where: string | undefined;
  schema: string | undefined;
  table: string | undefined;
  column: string | undefined;
  dataType: string | undefined;
  constraint: string | undefined;
  file: string | undefined;
  line: string | undefined;
  routine: string | undefined;

  get isUnique() {
    return this.code === '23505';
  }

  #columnsCache?: { [K in keyof T['shape']]?: true };
  get columns() {
    if (this.#columnsCache) return this.#columnsCache;

    const columns: { [K in keyof T['shape']]?: true } = {};

    if (this.detail) {
      const list = this.detail.match(/\((.*)\)=/)?.[1];
      if (list) {
        list.split(', ').forEach((item) => {
          const column = (
            item.startsWith('"') ? item.slice(1, -1) : item
          ) as keyof T['shape'];

          const key =
            queryColumnNameToKey(this.getQuery(), column as string) ?? column;
          columns[key as keyof T['shape']] = true;
        });
      }
    }

    return (this.#columnsCache = columns);
  }
}

export class MoreThanOneRowError extends OrchidOrmInternalError {
  constructor(query: IsQuery, message?: string) {
    super(query, message);
  }
}

export class UnhandledTypeError extends OrchidOrmInternalError {
  constructor(query: IsQuery, value: never) {
    super(query, `Unhandled type: ${JSON.stringify(value)} received`);
  }
}

/**
 * Error thrown when attempting to nest SQL session scopes.
 * Nested withOptions/$withOptions calls that supply role or setConfig while an outer
 * scope already has SQL session state defined will throw this error.
 */
export class NestedSqlSessionError extends OrchidOrmInternalError {
  constructor(query: IsQuery) {
    super(
      query,
      'Cannot nest SQL session scopes. Outer scope already has role or setConfig defined.',
    );
  }
}
